import { Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ESTADO_LABELS, ESTADOS_ORDENADOS, TIPO_LABELS, TIPOS_ORDENADOS } from '../lib/metas-format';
import type { GoalOption, GoalStatus, GoalType, GoalsFilters } from '../metas.types';
import { Select } from '@/components/ui/select';

interface MetasFiltrosProps {
  filtros: GoalsFilters;
  onFiltrosChange: (filtros: GoalsFilters) => void;
  /** Texto de búsqueda (se aplica en el cliente sobre el nombre). */
  busqueda: string;
  onBusquedaChange: (valor: string) => void;
  usuarios: GoalOption[];
  total: number;
}

const SELECT_CLASSES =
  'h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Filtros del listado de metas. */
export function MetasFiltros({
  filtros,
  onFiltrosChange,
  busqueda,
  onBusquedaChange,
  usuarios,
  total,
}: MetasFiltrosProps) {
  const hayFiltros =
    Boolean(filtros.status) || Boolean(filtros.goal_type) || Boolean(filtros.user_id) || busqueda !== '';

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="relative min-w-[14rem] flex-1">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400"
        />
        <input
          type="search"
          value={busqueda}
          onChange={(event) => onBusquedaChange(event.target.value)}
          placeholder="Buscar por nombre de meta…"
          aria-label="Buscar meta"
          className={cn(
            'h-10 w-full rounded-control border border-ink-200 bg-white pl-9.5 pr-9 text-sm text-ink-900',
            'placeholder:text-ink-400 transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
          )}
        />

        {busqueda !== '' ? (
          <button
            type="button"
            onClick={() => onBusquedaChange('')}
            aria-label="Limpiar búsqueda"
            className="absolute right-2.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-600"
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
        Estado
        <Select
          value={filtros.status ?? ''}
          onChange={(event) => onFiltrosChange({ ...filtros, status: event.target.value as GoalStatus | '' })}
          className={SELECT_CLASSES}
        >
          <option value="">Todos</option>
          {ESTADOS_ORDENADOS.map((estado) => (
            <option key={estado} value={estado}>
              {ESTADO_LABELS[estado]}
            </option>
          ))}
        </Select>
      </label>

      <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
        Tipo
        <Select
          value={filtros.goal_type ?? ''}
          onChange={(event) => onFiltrosChange({ ...filtros, goal_type: event.target.value as GoalType | '' })}
          className={SELECT_CLASSES}
        >
          <option value="">Todos</option>
          {TIPOS_ORDENADOS.map((tipo) => (
            <option key={tipo} value={tipo}>
              {TIPO_LABELS[tipo]}
            </option>
          ))}
        </Select>
      </label>

      <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
        Responsable
        <Select
          value={filtros.user_id ?? ''}
          onChange={(event) =>
            onFiltrosChange({ ...filtros, user_id: event.target.value ? Number(event.target.value) : '' })
          }
          className={cn(SELECT_CLASSES, 'max-w-[13rem]')}
        >
          <option value="">Todos</option>
          {usuarios.map((usuario) => (
            <option key={usuario.id} value={usuario.id}>
              {usuario.name ?? usuario.text ?? `Usuario ${usuario.id}`}
            </option>
          ))}
        </Select>
      </label>

      <span className="pb-2 text-xs text-ink-400">
        {total} {total === 1 ? 'meta' : 'metas'}
      </span>

      {hayFiltros ? (
        <button
          type="button"
          onClick={() => {
            onBusquedaChange('');
            onFiltrosChange({});
          }}
          className="mb-1.5 inline-flex items-center gap-1.5 rounded-control border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-600 transition-colors hover:border-brand-300 hover:text-brand-700"
        >
          <X className="size-3.5" aria-hidden="true" />
          Limpiar filtros
        </button>
      ) : null}
    </div>
  );
}
